import { commonUtils } from '@/helpers/commonUtils'
import { BaseVm } from '@/mobxTools'
import { buyVipService } from '@/services/buyVipService'
import { userService } from '@/services/userService'
import Decimal from 'decimal.js'
import { observable } from 'mobx'
import { PayQrDialogProps } from '.'

export class PayQrDialogVm extends BaseVm<PayQrDialogProps> {
  props!: PayQrDialogProps

  @observable visible = false
  @observable currentTabKey: 'wx_pub_qr' | 'alipay_qr' = 'alipay_qr'
  @observable qrUrl?: string
  @observable qrErr = false
  @observable qrLoading = false
  @observable isBatchPurchase = false

  // 折扣价格状态，用于保存使用折扣码后的价格
  @observable discountCodePercent: number | null = null
  @observable discountCodeData: {
    discountCode: string
    statusInfo: {
      status: 'none' | 'success' | 'error'
      msg: string
    }
  } = {
    discountCode: '',
    statusInfo: {
      status: 'none',
      msg: '',
    },
  }
  private timer?: any
  private sessionId?: string
  public onViewDestroy() {
    this.clearTimeout()
  }

  public onViewCreate(): void {
    if (!this.props.open || !this.props.plan) {
      this.clearTimeout()
      return
    }
    this.genOrderQrCode()
  }

  public async genOrderQrCode() {
    if (!userService.isLogin) {
      console.error('未找到用户登录态')
      return
    }

    this.isBatchPurchase = !!(this.props.plan?.count && this.props.plan?.count > 1)

    const isDiscountCode =
      this.discountCodeData.discountCode !== undefined &&
      this.discountCodeData.discountCode &&
      !this.isBatchPurchase

    // 常规模式：清空二维码状态
    if (!isDiscountCode) {
      this.clearTimeout()
      this.qrErr = false
      this.qrUrl = ''
      this.discountCodePercent = null
    }

    const sessionId = commonUtils.genId()
    this.sessionId = sessionId
    const selectPlan = this.props.plan
    try {
      this.qrLoading = true
      const { qr, orderId, money } = await buyVipService.createOrder({
        action: userService.userData?.versionData.version === 'free' ? 'buy' : 'renew',
        // chargeChannel: this.currentTabKey,
        chargeChannel:
          selectPlan.isSubscription && this.currentTabKey === 'alipay_qr'
            ? 'alipay_agreement_qr'
            : this.currentTabKey,
        planId: selectPlan.id,
        version: selectPlan.version,
        source: this.props.source,
        ...(isDiscountCode && { discountTicket: this.discountCodeData.discountCode }),
        ...(this.isBatchPurchase && { count: this.props.plan?.count }),
      })
      if (this.sessionId !== sessionId) {
        return
      }

      if (this.props.from === 'pricingPage') {
        userService.postHuiyiRecord({
          carrier: 'web-定价页',
          module: '定价页',
          function: `${
            selectPlan.version.charAt(0).toUpperCase() + selectPlan.version.slice(1)
          }付费弹窗`,
          position: `定价页-${
            selectPlan.version.charAt(0).toUpperCase() + selectPlan.version.slice(1)
          }付费弹窗`,
          action: '展现',
          element: '弹窗',
          remark: {
            feature: this.props.feature,
            orderId,
          },
        })
      }

      // 折扣码模式：更新成功状态
      if (isDiscountCode) {
        this.discountCodeData = {
          ...this.discountCodeData,
          statusInfo: {
            status: 'success',
            msg: '兑换成功',
          },
        }
        this.clearTimeout()
        // 使用 Decimal 计算折扣百分比，避免浮点数精度问题
        // money 是分，selectPlan.price 是元，需要统一单位
        this.discountCodePercent = new Decimal(money)
          .div(100)
          .div(selectPlan.price)
          .mul(100)
          .toDecimalPlaces(1, Decimal.ROUND_HALF_UP)
          .toNumber()

        // 使用 Decimal 将分转换为元，避免浮点数精度问题
        selectPlan.discountPrice = new Decimal(money).div(100).toNumber()
      }

      if (this.isBatchPurchase && this.props.plan?.count) {
        // 使用 Decimal 将分转换为元，避免浮点数精度问题
        selectPlan.price = new Decimal(money).div(100).toNumber()

        // 使用 Decimal 进行精确乘法计算，避免浮点数精度问题
        selectPlan.originPrice = new Decimal(selectPlan.originPrice)
          .mul(this.props.plan.count)
          .toNumber()
      }

      this.qrUrl = qr

      this.pollingOrderStatus(orderId, true)
    } catch (error) {
      console.log(error)
      // 根据模式选择不同的错误处理
      if (isDiscountCode) {
        // 折扣码模式：更新 statusInfo
        this.discountCodeData = {
          ...this.discountCodeData,
          statusInfo: {
            status: 'error',
            msg: commonUtils.formatErrorMsg(error, '兑换码无效'),
          },
        }
      } else {
        // 常规模式：更新 qrErr
        this.qrErr = true
      }
    } finally {
      this.qrLoading = false
    }
  }

  private async pollingOrderStatus(orderId: string, force = false, cnt = 0) {
    if (!this.props.open) {
      return
    }
    if (force) {
      try {
        const { status } = await buyVipService.pollOrderStatus(orderId)
        if (status === 'completed') {
          try {
            commonUtils.sendExtensionMessage('markPaid', {})
            await userService.updateUserInfo()
          } catch (error) {}
          if (this.props.paySuccess) {
            this.props.paySuccess(
              this.props.plan.timeType === 'year' ||
                this.props.plan.timeType === 'year_subscription'
            )
          }
          setTimeout(() => {
            if (this.props.onClose) {
              this.props.onClose()
            }
          })
          /** 停止轮询,给出成功提示 */
          this.clearTimeout()
          return
        }
        if (status === 'canceled' || status === 'refunded') {
          /** 停止轮询 */
          return
        }
        if (status === 'created') {
          /** 取消上一次轮询 */
          this.clearTimeout()
          this.timer = setTimeout(() => {
            this.pollingOrderStatus(orderId, true, 0)
          }, 1000)
        }
      } catch (error) {
        if (cnt > 5) {
          this.clearTimeout()
          this.qrErr = true
          /** 重试结束，停止轮询 */
          return
        }
        this.pollingOrderStatus(orderId, true, cnt + 1)
      }
    }
  }

  private clearTimeout() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  }
}
